import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { SectionReveal, ChapterHeading } from "./Shared";
import { useLang } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_DEFS = [
  { name: "full_name", type: "text", testid: "lead-input-name" },
  { name: "gym_name", type: "text", testid: "lead-input-gym" },
  { name: "city", type: "text", testid: "lead-input-city" },
  { name: "phone", type: "tel", testid: "lead-input-phone" },
  { name: "email", type: "email", testid: "lead-input-email" },
];

export default function LeadForm() {
  const { t } = useLang();
  const FIELDS = FIELD_DEFS.map((f) => ({ ...f, label: t.form.fields[f.name].label, placeholder: t.form.fields[f.name].ph }));
  const [form, setForm] = useState({ full_name: "", gym_name: "", city: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success(t.form.success);
      setForm({ full_name: "", gym_name: "", city: "", phone: "", email: "" });
    } catch (err) {
      toast.error(t.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="talep" className="relative overflow-hidden border-t border-[#9B7AE8]/15 bg-[#F3F0FB] py-28" data-testid="lead-form-section">
      <div className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(201,176,255,0.35),transparent_70%)] blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:items-start">
          <div>
            <ChapterHeading index="06" label={t.form.label} title={t.form.title} />
            <SectionReveal delay={0.1}>
              <p className="mt-8 text-xl font-light leading-relaxed text-[#221A3D] sm:text-2xl" data-testid="lead-form-callout">
                {t.form.a}
                <span className="font-medium text-[#9B7AE8]">{t.form.hi}</span>
                {t.form.b}
              </p>
              <img
                src="/assets/cta-visual.png"
                alt="Keep Shaking"
                data-testid="cta-sticker"
                className="animate-sticker mt-10 w-48 sm:w-56"
              />
            </SectionReveal>
          </div>

          <SectionReveal delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="lead-form"
              className="rounded-2xl border border-[#9B7AE8]/20 bg-white/85 p-7 shadow-[0_30px_90px_rgba(155,122,232,0.18)] backdrop-blur-md sm:p-9"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                {FIELDS.map((f) => (
                  <div key={f.name} className={f.name === "email" ? "sm:col-span-2" : ""}>
                    <label htmlFor={f.name} className="font-nav mb-2 block text-[10px] font-semibold uppercase tracking-[0.25em] text-[#8E85AC]">
                      {f.label}
                    </label>
                    <input
                      id={f.name}
                      data-testid={f.testid}
                      type={f.type}
                      required
                      value={form[f.name]}
                      onChange={set(f.name)}
                      placeholder={f.placeholder}
                      className="w-full rounded-xl border border-[#9B7AE8]/20 bg-[#FAF9FE] px-4 py-3.5 text-sm text-[#221A3D] placeholder:text-[#B4ACCB] outline-none transition-all duration-300 focus:border-[#9B7AE8] focus:shadow-[0_0_0_3px_rgba(201,176,255,0.35)]"
                    />
                  </div>
                ))}
              </div>
              <button
                type="submit"
                data-testid="lead-submit-button"
                disabled={loading}
                className="font-nav mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[#C9B0FF] py-4 text-xs font-bold uppercase tracking-[0.25em] text-[#221A3D] shadow-[0_14px_40px_rgba(155,122,232,0.4)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#9B7AE8] hover:text-white hover:shadow-[0_18px_50px_rgba(155,122,232,0.55)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} strokeWidth={2.5} />}
                {loading ? t.form.sending : t.form.submit}
              </button>
            </form>
          </SectionReveal>
        </div>
      </div>
    </section>
  );
}
